import { useCallback, useState } from 'react';
import { useMounted } from './useMounted';

export interface UseLoadingActions {
    setState(value: boolean): void;

    start(): void;

    stop(): void;

    promise<T>(promise: Promise<T>): Promise<T>;
}

/**
 * React helper hook allowing us dealing with loading states.
 * @param defaultState
 */
export function useLoading(defaultState = false): [boolean, UseLoadingActions] {
    const [state, state$] = useState<boolean>(defaultState);
    const isMounted = useMounted();

    const setState = useCallback(
        (value: boolean) => {
            if (isMounted()) {
                state$(value);
            }
        },
        [isMounted, state$],
    );

    const start = useCallback(() => setState(true), [setState]);
    const stop = useCallback(() => setState(false), [setState]);

    const promise = useCallback(
        async <T>(promise: Promise<T>): Promise<T> => {
            let result: any;
            start();

            try {
                result = await promise;
            } catch (e) {
                stop();
                throw e;
            }

            stop();

            return result;
        },
        [start, stop],
    );

    return [
        state,
        {
            setState,
            start,
            stop,
            promise,
        },
    ];
}
